import type { Paragraph, Table } from "docx";
import { RiskbedomningData, Fraga } from "@/types/riskbedomning";
import { filterNAQuestions, groupNAQuestionsByCategory } from "./utils/naQuestionUtils";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import {
  body,
  buildDocument,
  tableOfContents,
  factTable,
  h1,
  h2,
  table,
} from "@/lib/documentGenerator/docTheme";
import {
  REVISION_HEADERS,
  formatRevisionDate,
  revisionChangeSummary,
  revisionPhaseName,
} from "@/lib/documentGenerator/revisionColumns";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export interface RisksammanstallningExportOptions {
  includeProjectRoles?: boolean;
  includeResponsibilities?: boolean;
  includeSignatures?: boolean;
  includeNASummary?: boolean;
  includeRevisionHistory?: boolean;
}

const ANSVAR_ROWS: [string, string][] = [
  ["Byggherre", "Övergripande ansvar för projektet och att arbetsmiljökraven uppfylls"],
  ["Bas-P", "Samordning av säkerhets- och hälsoarbetet under projekteringsfasen"],
  ["Bas-U", "Samordning av säkerhets- och hälsoarbetet under utförandefasen"],
  ["Entreprenör", "Säkert utförande av byggarbeten enligt gällande föreskrifter"],
  ["Projektledare", "Koordinering av genomförande och kommunikation mellan parter"],
];

export const generateRisksammanstallningWord = async (
  riskbedomningData: RiskbedomningData,
  allQuestions: Fraga[] = [],
  options: RisksammanstallningExportOptions = {}
): Promise<void> => {
  const {
    includeProjectRoles = true,
    includeResponsibilities = true,
    includeSignatures = true,
    includeNASummary = true,
    includeRevisionHistory = true,
  } = options;

  const documentName = "Risksammanställning";
  const stamp = await createStampForCurrentUser({
    projectName: riskbedomningData.projektNamn || "",
    docNumber: "30b / 31",
    documentName,
  });

  const children: (Paragraph | Table)[] = [];

  children.push(...tableOfContents());
  children.push(h1("1. Projektbeskrivning"), body(riskbedomningData.projektbeskrivning || ""));

  if (includeProjectRoles) {
    children.push(
      h1("2. Projektroller"),
      factTable([
        ["Bas-P handläggare", riskbedomningData.handlaggandeBASP],
        ["Bas-U handläggare", riskbedomningData.basU],
        ["Projektledare", riskbedomningData.projektledare],
        ["Projektör", riskbedomningData.projektor || riskbedomningData.projektorer],
        ["Produktionsplanerare", riskbedomningData.produktionsplanerare],
      ])
    );
  }

  children.push(
    h1("3. Syfte"),
    body(riskbedomningData.syfte || ""),
    h1("4. Bakgrund"),
    body(riskbedomningData.bakgrund || ""),
    h1("5. Omfattning"),
    body(riskbedomningData.omfattning || "")
  );

  if (includeResponsibilities) {
    children.push(h1("6. Ansvar"));
    if (riskbedomningData.ansvar) children.push(body(riskbedomningData.ansvar));
    children.push(
      table(["Roll", "Ansvar"], ANSVAR_ROWS, { columnWidths: [2600, 6760] })
    );
  }

  if (includeNASummary && allQuestions.length > 0) {
    const naQuestions = filterNAQuestions(riskbedomningData, allQuestions);
    if (naQuestions.length > 0) {
      const grouped = groupNAQuestionsByCategory(naQuestions);
      children.push(
        h1("7. Ej tillämpliga frågor (N/A)"),
        body("Följande områden och frågor har bedömts som ej tillämpliga för detta projekt.")
      );
      Object.keys(grouped)
        .sort()
        .forEach((category) => {
          children.push(
            h2(category),
            table(
              ["Nr", "Fråga"],
              grouped[category].map((f, i) => [String(i + 1), f.text]),
              { columnWidths: [900, 8460] }
            )
          );
        });
    }
  }

  if (includeRevisionHistory) {
    const revRows: string[][] = [];
    Object.entries(riskbedomningData.svar || {}).forEach(([questionId, svar]) => {
      const question = allQuestions.find((q) => q.id === questionId);
      (svar?.revisions ?? []).forEach((rev) => {
        revRows.push([
          formatRevisionDate(rev.timestamp),
          question?.text ?? questionId,
          rev.userName || "Okänd",
          revisionPhaseName(rev),
          revisionChangeSummary(rev),
        ]);
      });
    });
    revRows.sort((a, b) => a[0].localeCompare(b[0]));

    children.push(
      h1("8. Revisionshistorik"),
      body(
        "Loggen visar vem som ändrat, i vilket skede ändringen gjordes samt kommentar/status för ändringen."
      ),
      table(
        [REVISION_HEADERS[0], "Fråga", REVISION_HEADERS[1], REVISION_HEADERS[2], REVISION_HEADERS[3]],
        revRows,
        { columnWidths: [1500, 2600, 1500, 1500, 2260] }
      )
    );
  }

  if (includeSignatures) {
    children.push(
      h1("Underskrift"),
      factTable([
        ["Upprättad av", riskbedomningData.upprattatAv],
        ["Ort", riskbedomningData.ort],
        ["Datum", riskbedomningData.datum || new Date().toLocaleDateString("sv-SE")],
        ["Namnförtydligande", riskbedomningData.namnfortydligande],
        ["Underskrift", ""],
      ])
    );
  }
  const footer = await documentFooter({ docNumber: "30b / 31", documentType: "risksammanstallning", includeRevisions: false });

  const doc = buildDocument({
    meta: {
      status: footer.status,
      version: footer.version,
      review: footer.review,
      docNumber: "30b / 31",
      documentName,
      projectName: riskbedomningData.projektNamn || "",
      createdBy: riskbedomningData.upprattatAv,
      date: riskbedomningData.datum,
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(
    doc,
    `Risksammanställning_${riskbedomningData.projektNamn || "Projekt"}.docx`,
    {
      documentType: "risksammanstallning",
      docNumber: "30b / 31",
      documentName: `Risksammanställning ${riskbedomningData.projektNamn || ""}`.trim(),
      snapshot: riskbedomningData,
      stamp,
    }
  );
};
